/* eslint-disable */

import React, { useState, useEffect } from 'react';

const API_BASE_URL = 'https://crosslisting-backend-aacranbzdyd3ffbd.centralindia-01.azurewebsites.net/api/v1';

const KpiDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [dateFilters, setDateFilters] = useState({
    actions: { from: '', to: '' },
    errors: { from: '', to: '' },
    api: { from: '', to: '' },
    performance: { from: '', to: '' },
    ai: { from: '', to: '' },
    jobs: { from: '', to: '' },
    system: { from: '', to: '' }
  });
  const [kpiData, setKpiData] = useState({
    totalSmartscanCompanies: 0,
    totalSmartscanProducts: 0,
    totalSmartlistingCompanies: 0,
    totalSmartlistingProducts: 0,
    totalActions: 0,
    totalErrors: 0,
    totalApiCalls: 0,
    totalAiCalls: 0,
    totalJobs: 0,
    avgPerformance: 0,
    successRate: 0,
    recentActions: [],
    errorLogs: [],
    apiLogs: [],
    performanceLogs: [],
    aiLogs: [],
    jobLogs: [],
    systemLogs: []
  });

  const msToMinutes = (ms, { decimals = 2 } = {}) => {
    const value = Number(ms);
    if (!Number.isFinite(value)) return '-';
    return (value / 60000).toFixed(decimals);
  };

  const updateDateFilter = (tableKey, field, value) => {
    setDateFilters((prev) => ({
      ...prev,
      [tableKey]: {
        ...prev[tableKey],
        [field]: value
      }
    }));
  };

  const filterRowsByDate = (rows, tableKey, dateField) => {
    const filter = dateFilters[tableKey];
    if (!filter) return rows;

    return rows.filter((row) => {
      const rawDate = row[dateField];
      if (!rawDate) return false;

      const rowDate = new Date(rawDate);
      if (Number.isNaN(rowDate.getTime())) return false;

      if (filter.from) {
        const fromDate = new Date(`${filter.from}T00:00:00`);
        if (rowDate < fromDate) return false;
      }

      if (filter.to) {
        const toDate = new Date(`${filter.to}T23:59:59.999`);
        if (rowDate > toDate) return false;
      }

      return true;
    });
  };

  const renderDateFilters = (tableKey) => (
    <div style={{
      display: 'flex',
      gap: '12px',
      flexWrap: 'wrap',
      marginBottom: '20px',
      alignItems: 'end'
    }}>
      <div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>From</div>
        <input
          type="date"
          value={dateFilters[tableKey].from}
          onChange={(e) => updateDateFilter(tableKey, 'from', e.target.value)}
          style={{
            padding: '8px 10px',
            border: '1px solid #ddd',
            borderRadius: '6px'
          }}
        />
      </div>
      <div>
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '6px' }}>To</div>
        <input
          type="date"
          value={dateFilters[tableKey].to}
          onChange={(e) => updateDateFilter(tableKey, 'to', e.target.value)}
          style={{
            padding: '8px 10px',
            border: '1px solid #ddd',
            borderRadius: '6px'
          }}
        />
      </div>
      <button
        onClick={() => setDateFilters((prev) => ({
          ...prev,
          [tableKey]: { from: '', to: '' }
        }))}
        style={{
          padding: '8px 12px',
          border: '1px solid #ddd',
          borderRadius: '6px',
          background: 'white',
          cursor: 'pointer'
        }}
      >
        Clear
      </button>
    </div>
  );

  const getAuthToken = () => {
    return localStorage.getItem('token') || localStorage.getItem('accessToken');
  };

  const getHeaders = () => ({
    'Content-Type': 'application/json',
    ...(getAuthToken() && { 'Authorization': `Bearer ${getAuthToken()}` })
  });

  // Fetch all data from backend GET endpoints
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch action logs
      const actionsResponse = await fetch(`${API_BASE_URL}/action?limit=100`, {
        headers: getHeaders()
      });
      const actionsData = await actionsResponse.json();
      
      // Fetch error logs
      const errorsResponse = await fetch(`${API_BASE_URL}/error?limit=100`, {
        headers: getHeaders()
      });
      const errorsData = await errorsResponse.json();
      
      // Fetch API logs
      const apiResponse = await fetch(`${API_BASE_URL}/api?limit=100`, {
        headers: getHeaders()
      });
      const apiData = await apiResponse.json();
      
      // Fetch performance logs
      const perfResponse = await fetch(`${API_BASE_URL}/performance?limit=100`, {
        headers: getHeaders()
      });
      const perfData = await perfResponse.json();
      
      // Fetch AI logs
      const aiResponse = await fetch(`${API_BASE_URL}/ai?limit=100`, {
        headers: getHeaders()
      });
      const aiData = await aiResponse.json();
      
      // Fetch job logs
      const jobResponse = await fetch(`${API_BASE_URL}/job?limit=100`, {
        headers: getHeaders()
      });
      const jobData = await jobResponse.json();
      
      // Fetch system logs
      const systemResponse = await fetch(`${API_BASE_URL}/system?limit=100`, {
        headers: getHeaders()
      });
      const systemData = await systemResponse.json();

      // Fetch usage summary stats
      const usageSummaryResponse = await fetch(`${API_BASE_URL}/stats/usage-summary`, {
        headers: getHeaders()
      });
      const usageSummaryData = await usageSummaryResponse.json();
      
      // Calculate KPIs
      const actions = actionsData.success ? actionsData.data : [];
      const errors = errorsData.success ? errorsData.data : [];
      const apiCalls = apiData.success ? apiData.data : [];
      const perfLogs = perfData.success ? perfData.data : [];
      const aiLogs = aiData.success ? aiData.data : [];
      const jobLogs = jobData.success ? jobData.data : [];
      const usageSummary = usageSummaryData.success ? usageSummaryData.data : {};
      
      // Calculate success rate
      const completedActions = actions.filter(a => a.status === 'SUCCESS' || a.status === 'FAILED');
      const successfulActions = actions.filter(a => a.status === 'SUCCESS').length;
      const successRate = completedActions.length > 0 
        ? ((successfulActions / completedActions.length) * 100).toFixed(1)
        : 0;
      
      // Calculate average performance
      const avgPerformance = perfLogs.length > 0
        ? msToMinutes(perfLogs.reduce((sum, p) => sum + (p.duration_ms || 0), 0) / perfLogs.length)
        : 0;
      
      setKpiData({
        totalSmartscanCompanies: usageSummary.smartscan?.total_companies || 0,
        totalSmartscanProducts: usageSummary.smartscan?.total_products || 0,
        totalSmartlistingCompanies: usageSummary.smartlisting?.total_companies || 0,
        totalSmartlistingProducts: usageSummary.smartlisting?.total_products || 0,
        totalActions: actions.length,
        totalErrors: errors.length,
        totalApiCalls: apiCalls.length,
        totalAiCalls: aiLogs.length,
        totalJobs: jobLogs.length,
        avgPerformance: avgPerformance,
        successRate: successRate,
        recentActions: actions.slice(0, 20),
        errorLogs: errors.slice(0, 20),
        apiLogs: apiCalls.slice(0, 20),
        performanceLogs: perfLogs.slice(0, 20),
        aiLogs: aiLogs.slice(0, 20),
        jobLogs: jobLogs.slice(0, 20),
        systemLogs: systemData.success ? systemData.data.slice(0, 20) : []
      });
      
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchDashboardData, 30000);
    return () => clearInterval(interval);
  }, []);

  const filteredActions = filterRowsByDate(kpiData.recentActions, 'actions', 'start_time');
  const filteredErrors = filterRowsByDate(kpiData.errorLogs, 'errors', 'created_at');
  const filteredApiLogs = filterRowsByDate(kpiData.apiLogs, 'api', 'created_at');
  const filteredPerformanceLogs = filterRowsByDate(kpiData.performanceLogs, 'performance', 'created_at');
  const filteredAiLogs = filterRowsByDate(kpiData.aiLogs, 'ai', 'created_at');
  const filteredJobLogs = filterRowsByDate(kpiData.jobLogs, 'jobs', 'created_at');
  const filteredSystemLogs = filterRowsByDate(kpiData.systemLogs, 'system', 'created_at');

  return (
    <div style={{ padding: '20px', background: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: 0, color: '#333' }}>KPI Dashboard</h1>
        <p style={{ color: '#666' }}>Real-time monitoring from database</p>
      </div>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '20px',
        marginBottom: '30px'
      }}>
        {/* SmartScan Companies Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #3f51b5'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total SmartScan Companies</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#3f51b5' }}>{kpiData.totalSmartscanCompanies}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>Unique SmartScan companies</div>
        </div>

        {/* SmartScan Products Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #009688'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total Products Uploading</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#009688' }}>{kpiData.totalSmartscanProducts}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>SmartScan products total</div>
        </div>

        {/* SmartListing Companies Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #8bc34a'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total SmartListing Companies</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#8bc34a' }}>{kpiData.totalSmartlistingCompanies}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>Unique SmartListing companies</div>
        </div>

        {/* SmartListing Products Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #795548'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total SmartListing Products</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#795548' }}>{kpiData.totalSmartlistingProducts}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>SmartListing products total</div>
        </div>

        {/* Total Actions Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #2196f3'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total Actions</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2196f3' }}>{kpiData.totalActions}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>All time actions</div>
        </div>

        {/* Total Errors Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #f44336'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total Errors</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#f44336' }}>{kpiData.totalErrors}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>Error logs count</div>
        </div>

        {/* API Calls Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #9c27b0'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>API Calls</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#9c27b0' }}>{kpiData.totalApiCalls}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>External API calls</div>
        </div>

        {/* AI Calls Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #e91e63'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>AI Calls</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#e91e63' }}>{kpiData.totalAiCalls}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>AI service calls</div>
        </div>

        {/* Performance Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #ff9800'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Avg Performance</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#ff9800' }}>{kpiData.avgPerformance} min</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>Average duration</div>
        </div>

        {/* Jobs Card */}
        <div style={{
          background: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderLeft: '4px solid #00bcd4'
        }}>
          <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Total Jobs</div>
          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#00bcd4' }}>{kpiData.totalJobs}</div>
          <div style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>Background jobs</div>
        </div>
      </div>

      {/* Success Rate Card */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>Success Rate</div>
        <div style={{ fontSize: '48px', fontWeight: 'bold', color: '#4caf50' }}>{kpiData.successRate}%</div>
        <div style={{
          width: '100%',
          height: '10px',
          background: '#e0e0e0',
          borderRadius: '5px',
          marginTop: '10px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${kpiData.successRate}%`,
            height: '100%',
            background: '#4caf50',
            borderRadius: '5px'
          }}></div>
        </div>
      </div>

      {/* Recent Actions Table */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Recent Actions</h3>
        {renderDateFilters('actions')}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action Type</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Module</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Progress</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Start Time</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Duration (min)</th>
              </tr>
            </thead>
            <tbody>
              {filteredActions.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No actions found for the selected date range.
                  </td>
                </tr>
              ) : (
                filteredActions.map((action) => (
                  <tr key={action.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{action.id}</td>
                    <td style={{ padding: '12px' }}>{action.action_type}</td>
                    <td style={{ padding: '12px' }}>{action.module || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: action.status === 'SUCCESS' ? '#4caf50' : 
                                   action.status === 'FAILED' ? '#f44336' : 
                                   action.status === 'IN_PROGRESS' ? '#ff9800' : '#9e9e9e',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {action.status}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {action.progress !== undefined && action.progress !== null ? `${action.progress}%` : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>{new Date(action.start_time).toLocaleString()}</td>
                    <td style={{ padding: '12px' }}>
                      {action.duration_ms !== undefined && action.duration_ms !== null
                        ? msToMinutes(action.duration_ms)
                        : 'In Progress'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error Logs Table */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Recent Errors</h3>
        {renderDateFilters('errors')}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Error Type</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Error Message</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Severity</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Step</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredErrors.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No errors found for the selected date range.
                  </td>
                </tr>
              ) : (
                filteredErrors.map((error) => (
                  <tr key={error.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{error.id}</td>
                    <td style={{ padding: '12px' }}>{error.action_id || '-'}</td>
                    <td style={{ padding: '12px' }}>{error.error_type || '-'}</td>
                    <td style={{ padding: '12px', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {error.error_message?.substring(0, 100)}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: error.severity === 'HIGH' ? '#f44336' :
                                   error.severity === 'MEDIUM' ? '#ff9800' : '#4caf50',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {error.severity || 'MEDIUM'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{error.step || '-'}</td>
                    <td style={{ padding: '12px' }}>{error.created_at ? new Date(error.created_at).toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* API Logs Table */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>API Calls Log</h3>
        {renderDateFilters('api')}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Service Name</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Endpoint</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Method</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status Code</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Duration (min)</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredApiLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No API calls found for the selected date range.
                  </td>
                </tr>
              ) : (
                filteredApiLogs.map((api) => (
                  <tr key={api.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{api.id}</td>
                    <td style={{ padding: '12px' }}>{api.service_name || '-'}</td>
                    <td style={{ padding: '12px', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {api.endpoint || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>{api.method || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: api.status_code >= 200 && api.status_code < 300 ? '#4caf50' :
                                   api.status_code >= 400 ? '#f44336' : '#ff9800',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {api.status_code || '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>
                      {api.duration_ms !== undefined && api.duration_ms !== null
                        ? msToMinutes(api.duration_ms)
                        : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>{api.created_at ? new Date(api.created_at).toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Performance Logs Table */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Performance Metrics</h3>
        {renderDateFilters('performance')}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Service Name</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Operation</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Duration (min)</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredPerformanceLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No performance logs found for the selected date range.
                  </td>
                </tr>
              ) : (
                filteredPerformanceLogs.map((perf) => (
                  <tr key={perf.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{perf.id}</td>
                    <td style={{ padding: '12px' }}>{perf.action_id || '-'}</td>
                    <td style={{ padding: '12px' }}>{perf.service_name || '-'}</td>
                    <td style={{ padding: '12px' }}>{perf.operation || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      {perf.duration_ms !== undefined && perf.duration_ms !== null
                        ? msToMinutes(perf.duration_ms)
                        : '-'}
                    </td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: perf.status === 'SUCCESS' ? '#4caf50' : '#f44336',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {perf.status || 'UNKNOWN'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{perf.created_at ? new Date(perf.created_at).toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* AI Logs Table */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>AI Service Logs</h3>
        {renderDateFilters('ai')}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>AI Service</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Success</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Confidence Score</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Input Quality</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Model Version</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredAiLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No AI logs found for the selected date range.
                  </td>
                </tr>
              ) : (
                filteredAiLogs.map((ai) => (
                  <tr key={ai.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{ai.id}</td>
                    <td style={{ padding: '12px' }}>{ai.ai_service || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: ai.success ? '#4caf50' : '#f44336',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {ai.success ? 'SUCCESS' : 'FAILED'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{ai.confidence_score || '-'}</td>
                    <td style={{ padding: '12px' }}>{ai.input_quality || '-'}</td>
                    <td style={{ padding: '12px' }}>{ai.model_version || '-'}</td>
                    <td style={{ padding: '12px' }}>{ai.created_at ? new Date(ai.created_at).toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Job Logs Table */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '30px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>Background Jobs</h3>
        {renderDateFilters('jobs')}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Action ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Job Type</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Retry Count</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Next Retry At</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredJobLogs.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No jobs found for the selected date range.
                  </td>
                </tr>
              ) : (
                filteredJobLogs.map((job) => (
                  <tr key={job.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{job.id}</td>
                    <td style={{ padding: '12px' }}>{job.action_id || '-'}</td>
                    <td style={{ padding: '12px' }}>{job.job_type || '-'}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: job.status === 'SUCCESS' ? '#4caf50' :
                                   job.status === 'FAILED' ? '#f44336' :
                                   job.status === 'PENDING' ? '#ff9800' : '#9e9e9e',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {job.status || 'PENDING'}
                      </span>
                    </td>
                    <td style={{ padding: '12px' }}>{`${job.retry_count || 0}/${job.max_retries || 3}`}</td>
                    <td style={{ padding: '12px' }}>{job.next_retry_at ? new Date(job.next_retry_at).toLocaleString() : '-'}</td>
                    <td style={{ padding: '12px' }}>{job.created_at ? new Date(job.created_at).toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* System Logs Table */}
      <div style={{
        background: 'white',
        borderRadius: '10px',
        padding: '20px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px' }}>System Logs</h3>
        {renderDateFilters('system')}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                <th style={{ padding: '12px', textAlign: 'left' }}>ID</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Level</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Message</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Service Name</th>
                <th style={{ padding: '12px', textAlign: 'left' }}>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredSystemLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                    No system logs found for the selected date range.
                  </td>
                </tr>
              ) : (
                filteredSystemLogs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '12px' }}>{log.id}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        background: log.level === 'ERROR' ? '#f44336' :
                                   log.level === 'WARN' ? '#ff9800' :
                                   log.level === 'INFO' ? '#2196f3' : '#9e9e9e',
                        color: 'white',
                        fontSize: '12px'
                      }}>
                        {log.level || 'INFO'}
                      </span>
                    </td>
                    <td style={{ padding: '12px', maxWidth: '400px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {log.message || '-'}
                    </td>
                    <td style={{ padding: '12px' }}>{log.service_name || '-'}</td>
                    <td style={{ padding: '12px' }}>{log.created_at ? new Date(log.created_at).toLocaleString() : '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Refresh Button */}
      <div style={{ position: 'fixed', bottom: '20px', right: '20px' }}>
        <button
          onClick={fetchDashboardData}
          disabled={loading}
          style={{
            background: '#2196f3',
            color: 'white',
            border: 'none',
            borderRadius: '5px',
            padding: '10px 20px',
            cursor: 'pointer',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
            opacity: loading ? 0.7 : 1
          }}
        >
          {loading ? 'Refreshing...' : 'Refresh Data'}
        </button>
      </div>
    </div>
  );
};

export default KpiDashboard;